
import { Component, signal, computed, effect, HostListener, ViewChild, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

// Angular Material
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { LookUpService } from '../../services/lookupservice';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicField } from '../../interfaces/dynamic-form-model';
import { forkJoin, max } from 'rxjs';
import { DataOverrideService } from '../../services/data-override';
import { ColumnMeta } from '../../models/dosing.model';
import { RegionsInfo, SitesShortForm } from '../../interfaces/regions';
import { DynamicFormRuleApplierService } from '../../services/dynamic-form-builder-rule-applier.service';
import { MatSort } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NotificationService } from '../../services/notificationservice';
import { dateRangeCompleteValidator, numericInputLimit, strictDateValidator } from '../shared/commonutils/helper';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { RoleService } from '../../services/role.service';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';

@Component({
  selector: 'app-dataoverride',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatDividerModule,
    MatSelectModule,
    MatDatepickerModule
  ],
  templateUrl: './dataoverride.component.html',
  styleUrl: './dataoverride.component.scss'
})
export class DataoverrideComponent {
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  displayedColumns: string[] = [];
  form!: FormGroup;
  private destroyRef = inject(DestroyRef);

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatSort) sort!: MatSort;
  selectedRowIndex: number | null = null;
  selectedColumnIndex: number = 1; // default after Date
  uiState!: Record<string, any>;
  columns!: DynamicField[];
  disposalSites = signal<SitesShortForm[]>([]);
  selectedSiteId = signal<number | null>(0);
  siteQuery = signal<string>('');
  markets = signal<RegionsInfo[]>([]);
  selectedMarketId = signal<number>(-1);
  marketQuery = signal<string>('');
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  isInitializing = signal(false);
  hasAnyChange: boolean = false;
  displayValue = signal<string>('');
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null, [strictDateValidator]),
    end: new FormControl<Date | null>(null, [strictDateValidator]),
  }, {validators: dateRangeCompleteValidator});

  constructor(private fb: FormBuilder,private dialog: MatDialog,
    private _lookupService: LookUpService,
    private _dataOverrideService: DataOverrideService,
    private _ruleApplier: DynamicFormRuleApplierService,
    private _notificationService: NotificationService,
  private rolesService: RoleService, private rpService: RolePermissionsService) {

    //Intitilize date range
    this.initializeDefaultFilters();

    this.form = this.fb.group({
      rows: this.fb.array([])
    });

    this.loadMarkets();

    //Selected Market effect
    effect(() => {
      if (!this.isInitializing()) return;
      const marketId = this.selectedMarketId();
      if (!marketId) return;
      this.siteQuery.set('');
      this.loadDisposalSites(marketId);
    });

    //Selected Disposal Site Effect
    effect(() => {
      if (!this.isInitializing()) return;
      const siteId = this.selectedSiteId();
      if (!siteId) return;

      this.getDosingOverrideData(siteId,
        this.range.get('start')?.value?.toISOString().split('T')[0] || '',
        this.range.get('end')?.value?.toISOString().split('T')[0] || '');

    });
  }
  private initializeDefaultFilters(): void {
    const today = new Date();
    const starttDateOfLastMonth = this.getStartOfLastMonth();
    this.range = new FormGroup({
      start: new FormControl<Date | null>(starttDateOfLastMonth),
      end: new FormControl<Date | null>(today)
    });
  }

  getStartOfLastMonth(): Date {
    const today = new Date();
    // Move to last month
    const lastMonth = today.getMonth() - 1;
    const year = lastMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = (lastMonth + 12) % 12; // handle January case
    return new Date(year, month, 1); // first day of last month
  }
  private loadMarkets(): void {
    let roles = this.rolesService.getRolesAsString();
    this.rpService.getRolePermissionsNew(roles, ScreenName.DataOverride, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
    this._lookupService.getMarketsLookup()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (regions: RegionsInfo[]) => {
          this.markets.set(regions);
          this.isInitializing.set(true);
        }
      })
  }
  // Filtered views (case-insensitive)
  marketsFiltered = computed(() => {
    const q = this.marketQuery().trim().toLowerCase();
    return this.markets().filter(m => m.regionName.toLowerCase().includes(q));
  });
  onMarketSelected(value: number): void {
    this.selectedMarketId.set(value);
  }
  onMarketQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.marketQuery.set(searchTerm);
  }
  sitesFiltered = computed(() => {
    const q = this.siteQuery().trim().toLowerCase();
    return this.disposalSites().filter(s => s.siteName.toLowerCase().includes(q));
  });
  onSiteSelected(value: number | null): void {
    this.selectedSiteId.set(value);
  }
  onSiteQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.siteQuery.set(searchTerm);
  }
  trackByMarketId = (_: number, item: RegionsInfo) => item.id;
  trackBySiteId = (_: number, item: SitesShortForm) => item.id;

  private loadDisposalSites(marketId: number): void {
    this._lookupService.getDataOverrideSites()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sites: SitesShortForm[]) => {
          console.log('Disposal Sites:', sites);
          this.disposalSites.set(sites);

          this.selectedSiteId.set(sites[0]?.id || 0);
        }
      })
  }

  ngOnInit() {

  }

  onSearch(): void {
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
    
    if(this.range.invalid){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    const siteId = this.selectedSiteId();
    const startDate = this.range.get('start')?.value;
    const endDate = this.range.get('end')?.value;

    if (siteId && startDate && endDate) {
      const fromDate = startDate.toISOString().split('T')[0];
      const toDate = endDate.toISOString().split('T')[0];
      this.getDosingOverrideData(siteId, fromDate, toDate);
    }
  }

  get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  refreshTable() {
    this.dataSource.data = this.rows.controls;
    this.dataSource.sort = this.sort;
  }

  /** 🔥 Build row dynamically from column config */
  createRow(data?: any) {
    const group: any = {};
    this.columns.forEach(col => {
      if (col.visible) {
        group[col.key] = [{ value: data?.[col.key] ?? '', disabled: col.readonly }];
      }
    });
    return this.fb.group(group);
  }

  private getDosingOverrideData(siteId: number, fromDate: string, toDate: string) {
    this.clearTable();

    forkJoin({
      uiSchema: this._lookupService.GetUISchema("PlantPerformanceDataoverride_Save"),
      apiResponse: this._dataOverrideService.getDataOverrideData(siteId, fromDate, toDate)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ uiSchema, apiResponse }) => {

        var sortColumns = [...uiSchema]
          .sort((a, b) => a.order - b.order);

        let ruleData = { "SiteID": this.selectedSiteId() }

        //Apply the rules
        this.uiState = this._ruleApplier.applyRulesForUpsertForm(sortColumns, ruleData);

        //Set the rules result
        sortColumns.forEach(element => {
          for (const prop in this.uiState) {
            if (prop === element.key) {
              element.visible = this.uiState[element.key].visible;
              element.readonly = this.uiState[element.key].readonly;
              element.required = this.uiState[element.key].required;
              element.label = this.uiState[element.key].label;
            }
          }
        });

        //Filter the visible fields
        this.columns = sortColumns.filter(x => x.visible == true);

        //Set displayed columns
        var excludeHiidenColumns = this.columns
          .filter((x) => x.type !== 'hidden')
          .filter((c, i, arr) => arr.findIndex(x => x.key === c.key) === i);

        this.displayedColumns = [...excludeHiidenColumns.map(c => c.key)];

        //Build the dynamic form
        apiResponse.forEach(row => {

          const formRow = this.createRow(row);

          //TRACKING CHANGES
          (formRow as any).__original = JSON.stringify(row);
          (formRow as any).__dirty = false;

          formRow.valueChanges.subscribe(() => {
            const isDirty =
              JSON.stringify(formRow.getRawValue()) !==
              (formRow as any).__original;

            (formRow as any).__dirty = isDirty;

            //  GLOBAL FLAG
            this.hasAnyChange = this.rows.controls.some(
              (r: any) => r.__dirty
            );
          });

          this.rows.push(formRow);
        });

      
        this.hasAnyChange = false;
        this.refreshTable();
      });
  }

  getChangedRows() {
    return this.rows.controls
      .filter((row: any) => row.__dirty)
      .map(row => {
        const payload: any = {};

        this.columns.forEach(col => {
          let value = row.getRawValue()[col.key];

          if (col.type === 'number' && value !== '' && value !== null) {
            let numValue = Number(value);
            const keyLower = col.key.toLowerCase();
            if (['h2s', 'biogas'].some(key => keyLower.includes(key))) {
              let h2sStr = String(value).trim();
              if (h2sStr.length > 6) {
                h2sStr = h2sStr.substring(0, 6);
              }
              payload[col.key] = h2sStr === '' ? null : parseInt(h2sStr, 10);
            }
            else if (['ch4', 'co2', 'o2'].some(key => keyLower.includes(key))) {
              numValue = Math.trunc(numValue * 100) / 100;
            }
            else if (keyLower.includes('methane_consumed')) {
              numValue = Math.trunc(numValue * 10000) / 10000;
            }
            payload[col.key] = numValue;
          }else {
            payload[col.key] = value === '' ? null : value;
          }

          // payload[col.key] =
          //   col.type === 'number'
          //     ? value === '' ? null : Number(value)
          //     : value;
        });

        return payload;
      });
  }

  onUpdate() {
    const payload = this.getChangedRows();
    console.log('Payload to save:', payload);
    this._dataOverrideService.saveDataOverride(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: boolean) => {
          if (result)
            this._notificationService.show('Data Saved Successfully!', 'success');
        }
      });
  }

  clearTable() {
    this.rows.clear();
    this.refreshTable();
    this.hasAnyChange = false;
  } 

  addRowBelow() {
    if (this.selectedRowIndex === null) return;
    this.rows.insert(this.selectedRowIndex + 1, this.createRow());
    this.refreshTable();
  }

  deleteRow() {
    if (this.selectedRowIndex === null) return;
    this.rows.removeAt(this.selectedRowIndex);
    this.refreshTable();
  }

  selectRow(i: number,colName: string) {
    this.selectedRowIndex = i;
    this.selectedColumnIndex = this.displayedColumns.indexOf(colName);
  }

  onRightClick(event: MouseEvent, index: number, colName: string) {
    event.preventDefault();
    this.selectRow(index, colName);
    this.menuTrigger.openMenu();
  }

  /** Excel paste (dynamic columns) */
  @HostListener('document:paste', ['$event'])
  handlePaste(event: ClipboardEvent) {
    if (this.selectedRowIndex === null) return;
    const text = event.clipboardData?.getData('text');
    if (!text) return;

    this.pasteExcel(text);
    event.preventDefault();
  }

 pasteExcel(text: string) {
  const lines = text.replace(/\r/g, '').split('\n').filter(Boolean);

  let rowIndex = this.selectedRowIndex ?? 0;
  let colStart = this.selectedColumnIndex ?? 1; // start from clicked column

  lines.forEach(line => {
    let row = this.rows.at(rowIndex);

    if (!row) {
      row = this.createRow();
      this.rows.push(row);
    }

    const cells = line.split('\t'); // ✅ take all cells (no skip)

    cells.forEach((value, i) => {
      const targetColIndex = colStart + i;

      // ✅ prevent overflow
      if (targetColIndex >= this.displayedColumns.length) return;

      // ✅ never overwrite Date column (index 0)
      if (targetColIndex === 0) return;

      const colName = this.displayedColumns[targetColIndex];

      row.get(colName)?.setValue(value ?? '');
    });

    rowIndex++;
  });

  this.refreshTable();
}



  getPayload() {
    return this.rows.getRawValue().map(row => {
      const payload: any = {};

      this.columns.forEach(col => {
        let value = row[col.key];

        if (col.type === 'number') {
          payload[col.key] =
            value === '' || value === null ? null : Number(value);
        } else {
          payload[col.key] = value;
        }
      });

      return payload;
    });
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart;
    let val = input.value;
    if (controlName.toLocaleLowerCase().includes('ch4') || controlName.toLocaleLowerCase().includes('co2')
      || controlName.toLocaleLowerCase().includes('o2')) {
      maxValue = 999;
      maxDecimals = 2;
    }
    if (controlName.toLocaleLowerCase().includes('h2s') || controlName.toLocaleLowerCase().includes('biogas')) {
      maxDecimals = 0;
    }
    var cleanedVal = numericInputLimit(event, maxValue, maxDecimals);
    if (val !== cleanedVal) {
      val = cleanedVal;
      input.value = cleanedVal;
      // Set the cursor back to where it was
      input.setSelectionRange(start, start);
    }
    this.displayValue.set(val);
    input.value = val;
  }
  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
